// Unicode font support for PDF reports.
// jsPDF's built-in Helvetica covers Latin-1 only. Noto Sans (loaded on demand
// from /public/fonts) covers Greek, Cyrillic and extended Latin. For CJK text,
// individual runs are routed to SimSun — a TTF proven to work with jsPDF's
// font parser — because Noto Sans has no CJK glyphs, while SimSun lacks some
// accented Greek letters (e.g. ά/έ/ό). Neither alone covers both families.

import { setUnicodeFontsActive } from './utils';

const CJK_RE = /[⺀-⿟぀-ヿ㐀-䶿一-鿿豈-﫿！-｠]/;
const NON_LATIN_RE = /[^\x00-\xFF]/;

const NOTO_FONTS = [
  { file: 'NotoSans-Regular.ttf', name: 'NotoSans', style: 'normal' },
  { file: 'NotoSans-Bold.ttf', name: 'NotoSans', style: 'bold' },
  { file: 'NotoSans-Italic.ttf', name: 'NotoSans', style: 'italic' },
];
const CJK_FONT = { file: 'SimSun.ttf', name: 'NotoCJK', style: 'normal' };

const _b64Cache = {};

async function _fetchFontB64(file) {
  if (_b64Cache[file]) return _b64Cache[file];
  const res = await fetch(`${process.env.PUBLIC_URL || ''}/fonts/${file}`);
  if (!res.ok) throw new Error(`Could not load font: ${file}`);
  const bytes = new Uint8Array(await res.arrayBuffer());
  let bin = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  const b64 = btoa(bin);
  _b64Cache[file] = b64;
  return b64;
}

// Route every setFont('Helvetica', ...) call to Noto Sans so the 13 page
// renderers (and jspdf-autotable, which sets styles.font per cell) pick it up
// with zero changes. When CJK is present in the tenant's data, individual text
// runs containing CJK characters are additionally routed to SimSun.
function _patchDocFonts(doc, { cjk }) {
  const origSetFont = doc.setFont.bind(doc);

  doc.setFont = (name, style) => {
    if (typeof name === 'string' && name.toLowerCase() === 'helvetica') {
      let s = (style || 'normal').toLowerCase();
      if (s === 'bolditalic') s = 'bold';
      return origSetFont('NotoSans', s);
    }
    return origSetFont(name, style);
  };

  if (cjk) {
    const routeCjk = (fn) => function (...args) {
      const probe = Array.isArray(args[0]) ? args[0].join('\n') : String(args[0] ?? '');
      if (!CJK_RE.test(probe)) return fn.apply(this, args);
      const prev = doc.getFont();
      origSetFont(CJK_FONT.name, 'normal');
      try {
        return fn.apply(this, args);
      } finally {
        origSetFont(prev.fontName, prev.fontStyle);
      }
    };
    doc.text = routeCjk(doc.text.bind(doc));
    doc.getTextWidth = routeCjk(doc.getTextWidth.bind(doc));
    doc.splitTextToSize = routeCjk(doc.splitTextToSize.bind(doc));
  }

  if (typeof doc.autoTableSetDefaults === 'function') {
    doc.autoTableSetDefaults({ styles: { font: 'NotoSans' } });
  }
}

async function _activate(doc, { cjk }) {
  for (const f of NOTO_FONTS) {
    const b64 = await _fetchFontB64(f.file);
    doc.addFileToVFS(f.file, b64);
    doc.addFont(f.file, f.name, f.style);
  }
  if (cjk) {
    const b64 = await _fetchFontB64(CJK_FONT.file);
    doc.addFileToVFS(CJK_FONT.file, b64);
    doc.addFont(CJK_FONT.file, CJK_FONT.name, CJK_FONT.style);
  }
  _patchDocFonts(doc, { cjk });
  doc.setFont('Helvetica', 'normal');
  setUnicodeFontsActive(true);
}

export function detectScripts(text) {
  const t = text || '';
  return { unicode: NON_LATIN_RE.test(t), cjk: CJK_RE.test(t) };
}

// For report builders that have an API client: probe the tenant's data for
// non-Latin scripts before rendering.
export async function applyUnicodeFontSupport(doc, api, extraText = '') {
  let sample = String(extraText || '');
  try {
    const res = await api.get('/analytics/text-probe');
    sample += ' ' + ((res && res.data && res.data.sample) || '');
  } catch { /* probe is best-effort */ }
  const { unicode, cjk } = detectScripts(sample);
  if (!unicode) return false;
  await _activate(doc, { cjk });
  return true;
}

// For builders that already hold the full payload (e.g. exportUtils).
export async function applyUnicodeFontSupportFromText(doc, payload) {
  const text = typeof payload === 'string' ? payload : JSON.stringify(payload || '');
  const { unicode, cjk } = detectScripts(text);
  if (!unicode) return false;
  await _activate(doc, { cjk });
  return true;
}
